import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';

import type { MisconfiguredConnector } from '../utils/validateConnectors';

interface MisconfiguredConnectorModalProps {
  misconfiguredConnectors: MisconfiguredConnector[];
  onClose: () => void;
}

/**
 * Modal that displays when environment has misconfigured connectors.
 * A connector is misconfigured when there are multiple credentials of the same type
 * but none have been marked as the default connection.
 *
 * Matches dataProtect/src/components/environments/Connector.js lines 895-942
 */
export const MisconfiguredConnectorModal = ({
  misconfiguredConnectors,
  onClose,
}: MisconfiguredConnectorModalProps) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show modal if there are misconfigured connectors
    if (misconfiguredConnectors.length > 0) {
      setOpen(true);
    }
  }, [misconfiguredConnectors]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  if (misconfiguredConnectors.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Action Required!</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ fontSize: 18, mb: 2 }}>
            This environment currently has <strong>{misconfiguredConnectors.length}</strong>{' '}
            misconfigured connector type(s).
          </Typography>
          <Typography sx={{ fontSize: 16, mb: 2 }}>
            Please review following connection(s) and{' '}
            <strong>mark one of the connection as default connection.</strong> (
            <Link
              href="https://help.lingk.io/en/articles/331"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              know more
            </Link>
            )
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 0 }}>
            {misconfiguredConnectors.map((connector) => (
              <Typography
                component="li"
                key={connector.credentialType}
                sx={{ fontSize: 16, fontWeight: 500 }}
              >
                {connector.credentialType}
              </Typography>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          variant="contained"
          onClick={handleClose}
          sx={{
            bgcolor: 'grey.800',
            color: 'white',
            '&:hover': {
              bgcolor: 'grey.900',
            },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
